import {
  AttributeGroupDraft,
  ProductModifierGroupDraft,
  ProductVariantEditor,
} from '@features/catalog/models/product-form-draft.models';
import { Product } from '@features/catalog/models/product.models';
import { CreateProductBundleInput } from '@features/catalog/models/product-input.models';
import {
  buildCreateBundleInput,
  ProductBundleFormRawValue,
} from '@features/catalog/utils/product-bundle-input.builder';
import {
  omitUnchangedBundleStructureFields,
  shouldOmitBundleStructureFieldsOnUpdate,
  shouldUseProductBundleUpdate,
} from '@features/catalog/utils/product-form-publication.utils';
import { MediaUploadResult } from '@core/media/models/media-upload.models';
import { MediaUploadService } from '@core/media/services/media-upload.service';
import { Injectable, inject } from '@angular/core';
import { Observable, forkJoin, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { CatalogProductService } from './catalog-product.service';

export interface PrepareProductBundleInputRequest {
  formValue: ProductBundleFormRawValue;
  attributeGroups: readonly AttributeGroupDraft[];
  productVariants: readonly ProductVariantEditor[];
  modifierGroups: readonly ProductModifierGroupDraft[];
  attributeValueIds: ReadonlySet<string>;
  variantUntitledLabel: string;
  lockVersion?: number;
  isEdit: boolean;
  productId: string | null;
  hasStructureChanges: boolean;
  /** Solo se envían campos de anticipación de entrega si el plan de la compañía incluye el módulo. */
  fulfillmentLeadTimeEnabled?: boolean;
  /** Rail Inventario usable: si false, no se envía inventoryControl (stock básico). */
  inventoryModuleEnabled?: boolean;
}

export interface ProductFormPostCreateUploadRequest {
  productId: string;
  companyId: number | string | null;
  productImageFile: File | null;
  galleryFiles: readonly File[];
  galleryStartPosition: number;
}

export interface ProductFormPostCreateUploadResult {
  mainImage: MediaUploadResult | null;
  galleryItems: Array<{
    id: string;
    deliveryUrl: string;
    position: number;
  }>;
}

export interface ProductFormSubmitRequest extends PrepareProductBundleInputRequest {
  postCreateUploads?: ProductFormPostCreateUploadRequest | null;
}

export interface ProductFormSubmitResult {
  kind: 'created' | 'updated';
  product: Product | null;
  errors: string[];
  postCreateUploads?: ProductFormPostCreateUploadResult;
}

@Injectable()
export class ProductFormSaveCoordinatorService {
  private readonly productService = inject(CatalogProductService);
  private readonly mediaUpload = inject(MediaUploadService);

  prepareBundleInput(request: PrepareProductBundleInputRequest): CreateProductBundleInput {
    let input = buildCreateBundleInput(
      request.formValue,
      [...request.attributeGroups],
      [...request.productVariants],
      [...request.modifierGroups],
      request.attributeValueIds,
      request.variantUntitledLabel,
      request.lockVersion,
      request.fulfillmentLeadTimeEnabled,
      request.inventoryModuleEnabled
    );

    if (
      shouldOmitBundleStructureFieldsOnUpdate(
        request.hasStructureChanges,
        request.formValue.productStructure,
        request.productVariants.length
      )
    ) {
      input = omitUnchangedBundleStructureFields(input);
    }

    return input;
  }

  submit(request: ProductFormSubmitRequest): Observable<ProductFormSubmitResult> {
    const input = this.prepareBundleInput(request);

    if (shouldUseProductBundleUpdate(request.isEdit, request.productId)) {
      return this.productService
        .updateBundle({
          id: request.productId!,
          input,
        })
        .pipe(
          switchMap((result) =>
            this.persistDimensions(request.productId!, request.formValue, result.product).pipe(
              switchMap((product) =>
                this.persistSupplySources(request.productId!, request.formValue, product)
              ),
              map((product) => ({
                kind: 'updated' as const,
                product,
                errors: result.errors,
              }))
            )
          )
        );
    }

    return this.productService.createBundle(input).pipe(
      switchMap((result) => {
        const createdProductId = result.product?.id?.trim() ?? '';
        const uploads = request.postCreateUploads;

        return this.persistDimensions(createdProductId, request.formValue, result.product).pipe(
          switchMap((product) => this.persistSupplySources(createdProductId, request.formValue, product)),
          switchMap((product) => {
            if (!createdProductId || !uploads) {
              return of({
                kind: 'created' as const,
                product,
                errors: result.errors,
              });
            }

            return this.uploadPostCreateAssets({
              ...uploads,
              productId: createdProductId,
            }).pipe(
              map((postCreateUploads) => ({
                kind: 'created' as const,
                product,
                errors: result.errors,
                postCreateUploads,
              }))
            );
          })
        );
      })
    );
  }

  /**
   * `createProductBundle`/`updateProductBundle` no aceptan peso/dimensiones, así que se
   * persisten con `updateProduct` en una llamada aparte una vez que el producto tiene id.
   */
  private persistDimensions(
    productId: string,
    formValue: ProductBundleFormRawValue,
    fallbackProduct: Product | null
  ): Observable<Product | null> {
    const hasDimensionFields =
      formValue.weightGrams !== undefined ||
      formValue.lengthCm !== undefined ||
      formValue.widthCm !== undefined ||
      formValue.heightCm !== undefined;

    if (!productId || !hasDimensionFields) {
      return of(fallbackProduct);
    }

    return this.productService
      .updateDimensions(productId, {
        weightGrams: formValue.weightGrams ?? null,
        lengthCm: formValue.lengthCm ?? null,
        widthCm: formValue.widthCm ?? null,
        heightCm: formValue.heightCm ?? null,
      })
      .pipe(
        map((dimensionsResult) => {
          if (!dimensionsResult.product || !fallbackProduct) {
            return fallbackProduct;
          }
          // Solo se fusionan los campos de dimensiones: la respuesta de `updateProduct`
          // aquí es parcial (no trae categorías, variantes, etc.).
          const { weightGrams, lengthCm, widthCm, heightCm } = dimensionsResult.product;
          return { ...fallbackProduct, weightGrams, lengthCm, widthCm, heightCm };
        })
      );
  }

  /**
   * `createProductBundle`/`updateProductBundle` no aceptan proveedores de insumo: se
   * persisten con `syncProductSupplySources` en una llamada aparte una vez que el
   * producto (y sus variantes) tienen id, igual que `persistDimensions`.
   */
  private persistSupplySources(
    productId: string,
    formValue: ProductBundleFormRawValue,
    fallbackProduct: Product | null
  ): Observable<Product | null> {
    if (!productId || !formValue.isSupplyItem || !formValue.supplySources?.length) {
      return of(fallbackProduct);
    }

    return this.productService.syncSupplySources(productId, formValue.supplySources).pipe(
      map((result) => {
        if (!fallbackProduct) {
          return fallbackProduct;
        }
        // Igual que `persistDimensions`: la respuesta es parcial, solo se fusionan
        // los campos que esta mutación efectivamente actualiza.
        return { ...fallbackProduct, isSupplyItem: result.isSupplyItem, supplySources: result.supplySources };
      })
    );
  }

  uploadPostCreateAssets(
    request: ProductFormPostCreateUploadRequest
  ): Observable<ProductFormPostCreateUploadResult> {
    const { productId, companyId, productImageFile, galleryFiles, galleryStartPosition } = request;

    if (!companyId) {
      return of({ mainImage: null, galleryItems: [] });
    }

    const mainImage$ = productImageFile
      ? this.mediaUpload.uploadImage(productImageFile, {
          kind: 'product_main',
          companyId: String(companyId),
          productId,
        })
      : of(null);

    const gallery$ =
      galleryFiles.length > 0
        ? forkJoin(
            galleryFiles.map((file) =>
              this.mediaUpload.uploadImage(file, {
                kind: 'product_gallery',
                companyId: String(companyId),
                productId,
              })
            )
          )
        : of([] as MediaUploadResult[]);

    return forkJoin({ mainImage: mainImage$, gallery: gallery$ }).pipe(
      map(({ mainImage, gallery }) => ({
        mainImage,
        galleryItems: gallery
          .filter((item) => item.mediaAssetId)
          .map((item, index) => ({
            id: item.mediaAssetId!,
            deliveryUrl: item.deliveryUrl,
            position: galleryStartPosition + index,
          })),
      }))
    );
  }
}